/**
 * PSAT/NMSQT National Merit Selection Index.
 *
 * Source: College Board, "What Do My SAT Suite Scores Mean?" - "NMSC Selection Index for
 * PSAT/NMSQT only": "Your score is calculated by doubling your Reading and Writing Score,
 * adding it to your Math score, then dividing that sum by 10."
 * https://satsuite.collegeboard.org/psat-nmsqt/scores/understanding-scores/your-score-explained
 * Retrieved 2026-10-16. The same page lists PSAT/NMSQT section scores as 160-760, the total as
 * 320-1520 and the NMSC Selection Index as "on a scale of 48-228".
 *
 * NMSC's own pages (nationalmerit.org) sit behind a login redirect for automated requests, so
 * its state Semifinalist cutoff tables could not be retrieved and are not included.
 */

export const PSAT_SECTION_MIN = 160;
export const PSAT_SECTION_MAX = 760;
export const SELECTION_INDEX_MIN = 48;
export const SELECTION_INDEX_MAX = 228;

/** A PSAT/NMSQT section score: 160-760 in steps of 10. */
export function isPsatSection(value: number | null): value is number {
  return (
    value !== null &&
    Number.isInteger(value) &&
    value >= PSAT_SECTION_MIN &&
    value <= PSAT_SECTION_MAX &&
    value % 10 === 0
  );
}

/** (2 x Reading and Writing + Math) / 10. A whole number for any valid pair of scores. */
export function selectionIndex(readingWriting: number, math: number): number {
  return (2 * readingWriting + math) / 10;
}

export interface IndexParts {
  index: number;
  /** Points of the index that come from doubled Reading and Writing. */
  fromReadingWriting: number;
  fromMath: number;
}

export function indexParts(readingWriting: number, math: number): IndexParts {
  return {
    index: selectionIndex(readingWriting, math),
    fromReadingWriting: (2 * readingWriting) / 10,
    fromMath: math / 10,
  };
}

export function isSelectionIndex(value: number | null): value is number {
  return (
    value !== null &&
    Number.isInteger(value) &&
    value >= SELECTION_INDEX_MIN &&
    value <= SELECTION_INDEX_MAX
  );
}

/** The lowest Math score that reaches `target` with the given Reading and Writing score. */
export function mathNeeded(
  readingWriting: number,
  target: number
): number | null {
  const needed = 10 * target - 2 * readingWriting;
  const score = Math.max(PSAT_SECTION_MIN, Math.ceil(needed / 10) * 10);
  return score > PSAT_SECTION_MAX ? null : score;
}

/** The lowest Reading and Writing score that reaches `target` with the given Math score. */
export function readingWritingNeeded(
  math: number,
  target: number
): number | null {
  const needed = (10 * target - math) / 2;
  const score = Math.max(PSAT_SECTION_MIN, Math.ceil(needed / 10) * 10);
  return score > PSAT_SECTION_MAX ? null : score;
}
